import { API_BASE_URL } from "../config/api";
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { logout } from "../utils/auth";

const resolveAccountsNotificationRoute = (n) => {
  if (!n) return "/accounts-dashboard";
  if (n.type === "report-submitted" || n.type === "travel-created" || n.type === "travel-status") return "/accounts/complaints";
  if (n.relatedId) return `/accounts/complaints?complaintId=${n.relatedId}`;
  return "/accounts-dashboard";
};

export default function AccountsLayout() {
  const navigate     = useNavigate();
  const location     = useLocation();
  const accountsName = localStorage.getItem("accountsName") || "Accounts";

  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
  const [expandedSection, setExpandedSection] = useState(null);
  const [profileOpen, setProfileOpen]         = useState(false);
  const [notifOpen, setNotifOpen]             = useState(false);
  const [notifications, setNotifications]     = useState([]);

  const desktopNotifRef = useRef(null);
  const mobileNotifRef  = useRef(null);
  const desktopProfileRef = useRef(null);
  const mobileProfileRef  = useRef(null);
  const sidebarRef = useRef(null);

  /* notifications */
  useEffect(() => {
    axios.get(API_BASE_URL + "/api/notifications", { params: { role: "admin" } })
      .then(r => setNotifications(Array.isArray(r.data) ? r.data : []))
      .catch(() => setNotifications([]));
  }, []);

  /* outside-click close */
  useEffect(() => {
    const h = (e) => {
      const clickedNotification =
        desktopNotifRef.current?.contains(e.target) ||
        mobileNotifRef.current?.contains(e.target);
      const clickedProfile =
        desktopProfileRef.current?.contains(e.target) ||
        mobileProfileRef.current?.contains(e.target);
      const clickedSidebar = sidebarRef.current?.contains(e.target);

      if (!clickedNotification) setNotifOpen(false);
      if (!clickedProfile) setProfileOpen(false);

      if (window.innerWidth < 1024 && !clickedSidebar && !e.target.closest("button[data-sidebar-toggle]")) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    document.addEventListener("touchstart", h);
    return () => {
      document.removeEventListener("mousedown", h);
      document.removeEventListener("touchstart", h);
    };
  }, []);

  /* auto-expand current section based on route */
  useEffect(() => {
    const p = location.pathname;
    if (["/accounts/complaints"].some(path => p === path || p.startsWith(`${path}/`))) {
      setExpandedSection("operations");
    } else if (["/accounts/quotations", "/accounts/performa-invoices", "/accounts/sales-invoices", "/accounts/purchase-orders", "/accounts/purchase-invoices", "/accounts/delivery-challans", "/accounts/project-expenses"].some(path => p === path || p.startsWith(`${path}/`))) {
      setExpandedSection("sales-purchase");
    } else if (["/accounts/vouchers", "/accounts/gst-export-reports", "/accounts/debit-credit-notes", "/accounts/material-stock", "/accounts/product-mapping"].some(path => p === path || p.startsWith(`${path}/`))) {
      setExpandedSection("accounts");
    } else if (["/accounts/dealers", "/accounts/customers", "/accounts/customer-groups"].some(path => p === path || p.startsWith(`${path}/`))) {
      setExpandedSection("contacts");
    }
  }, [location.pathname]);

  const unreadCount = useMemo(() => notifications.filter(n => !n.isRead).length, [notifications]);

  const markRead = async (notification) => {
    try {
      if (notification?._id && !notification.isRead) {
        await axios.put(API_BASE_URL + `/api/notifications/${notification._id}/read`);
        setNotifications(c => c.map(n => n._id === notification._id ? { ...n, isRead: true } : n));
      }
    } catch (e) { console.error(e); }
    finally { setNotifOpen(false); navigate(resolveAccountsNotificationRoute(notification)); }
  };

  const clearAllNotifications = async () => {
    try {
      await axios.delete(API_BASE_URL + "/api/notifications", { params: { role: "admin" } });
      setNotifications([]);
    } catch (e) { console.error(e); }
  };

  const handleLogout = () => {
    logout(navigate);
  };

  return (
    <div className="min-h-screen bg-white text-stone-900">
      {/* Overlay for Mobile/Collapsed Sidebar */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          id="sidebar-overlay" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar Drawer */}
      <aside 
        ref={sidebarRef}
        className={`fixed left-0 top-0 bottom-0 flex flex-col py-6 bg-stone-950 text-stone-100 w-[280px] z-50 overflow-y-auto transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`} 
        id="sidebar"
      >
        <div className="px-6 mb-8 flex justify-between items-center">
          <div>
            <h1 className="font-headline-lg text-headline-lg font-bold text-amber-300">GENMECH</h1>
            <p className="font-label-caps text-label-caps text-amber-500/60 uppercase tracking-widest mt-1">Accounts</p>
          </div>
          <button 
            className="text-stone-300 hover:text-white lg:hidden flex items-center justify-center" 
            onClick={() => setSidebarOpen(false)}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <nav className="flex-1 space-y-1">
          {/* Dashboard Link */}
          <NavLink 
            to="/accounts-dashboard"
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) => `mx-2 py-2 px-4 flex items-center gap-4 mb-2 rounded-lg transition-colors border-2 ${
              isActive 
                ? "bg-amber-500 text-stone-950 border-amber-400 font-bold shadow-md"
                : "text-stone-300 hover:text-white hover:bg-stone-900 border-transparent"
            }`}
          >
            <span className="material-symbols-outlined" style={{ fontVariationSettings: location.pathname === "/accounts-dashboard" ? "'FILL' 1" : "'FILL' 0" }}>dashboard</span>
            <span className="font-label-caps text-label-caps">Dashboard</span>
          </NavLink>

          {/* Dashboard Alerts Link */}
          <NavLink 
            to="/accounts/dashboard-alerts"
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) => `mx-2 py-2 px-4 flex items-center gap-4 mb-2 rounded-lg transition-colors border-2 ${
              isActive 
                ? "bg-amber-500 text-stone-950 border-amber-400 font-bold shadow-md"
                : "text-stone-300 hover:text-white hover:bg-stone-900 border-transparent"
            }`}
          >
            <span className="material-symbols-outlined" style={{ fontVariationSettings: location.pathname === "/accounts/dashboard-alerts" ? "'FILL' 1" : "'FILL' 0" }}>notifications_active</span>
            <span className="font-label-caps text-label-caps">Dashboard Alerts</span>
          </NavLink>

          {/* Accounts Monitor Link */}
          <NavLink 
            to="/accounts/accounts-monitor"
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) => `mx-2 py-2 px-4 flex items-center gap-4 mb-2 rounded-lg transition-colors border-2 ${
              isActive 
                ? "bg-amber-500 text-stone-950 border-amber-400 font-bold shadow-md"
                : "text-stone-300 hover:text-white hover:bg-stone-900 border-transparent"
            }`}
          >
            <span className="material-symbols-outlined" style={{ fontVariationSettings: location.pathname === "/accounts/accounts-monitor" ? "'FILL' 1" : "'FILL' 0" }}>monitoring</span>
            <span className="font-label-caps text-label-caps">Accounts Monitor</span>
          </NavLink>

          {/* Operations Accordion */}
          <div className="mb-2">
            <button
              type="button"
              onClick={() => setExpandedSection(prev => prev === "operations" ? null : "operations")}
              className={`w-[calc(100%-16px)] mx-2 py-2 px-4 flex items-center justify-between rounded-lg transition-colors border-2 ${
                ["/accounts/complaints", "/accounts/create-complaint"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))
                  ? "bg-amber-500/10 text-amber-300 border-amber-500/30 font-bold"
                  : "text-stone-300 hover:text-white hover:bg-stone-900 border-transparent"
              }`}
            >
              <div className="flex items-center gap-4">
                <span className="material-symbols-outlined" style={{ fontVariationSettings: ["/accounts/complaints", "/accounts/create-complaint"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`)) ? "'FILL' 1" : "'FILL' 0" }}>engineering</span>
                <span className="font-label-caps text-label-caps">Operations</span>
              </div>
              <span className="material-symbols-outlined transition-transform duration-200" style={{ transform: expandedSection === "operations" ? "rotate(180deg)" : "rotate(0deg)" }}>
                expand_more
              </span>
            </button>
            {expandedSection === "operations" && (
              <div className="ml-8 pl-4 border-l border-stone-800 flex flex-col gap-2 mt-2">
                <NavLink 
                  to="/accounts/complaints"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                    isActive ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                  }`}
                >
                  Complaints &amp; Reports
                </NavLink>
                <NavLink 
                  to="/accounts/create-complaint"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                    isActive ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                  }`}
                >
                  Create Complaint
                </NavLink>
              </div>
            )}
          </div>

          {/* Sales & Purchase Accordion */}
          <div className="mb-2">
            <button
              type="button"
              onClick={() => setExpandedSection(prev => prev === "sales-purchase" ? null : "sales-purchase")}
              className={`w-[calc(100%-16px)] mx-2 py-2 px-4 flex items-center justify-between rounded-lg transition-colors border-2 ${
                ["/accounts/quotations", "/accounts/performa-invoices", "/accounts/sales-invoices", "/accounts/purchase-orders", "/accounts/purchase-invoices", "/accounts/delivery-challans", "/accounts/project-expenses"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))
                  ? "bg-amber-500/10 text-amber-300 border-amber-500/30 font-bold"
                  : "text-stone-300 hover:text-white hover:bg-stone-900 border-transparent"
              }`}
            >
              <div className="flex items-center gap-4">
                <span className="material-symbols-outlined" style={{ fontVariationSettings: ["/accounts/quotations", "/accounts/performa-invoices", "/accounts/sales-invoices", "/accounts/purchase-orders", "/accounts/purchase-invoices", "/accounts/delivery-challans", "/accounts/project-expenses"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`)) ? "'FILL' 1" : "'FILL' 0" }}>payments</span>
                <span className="font-label-caps text-label-caps">Sales &amp; Purchase</span>
              </div>
              <span className="material-symbols-outlined transition-transform duration-200" style={{ transform: expandedSection === "sales-purchase" ? "rotate(180deg)" : "rotate(0deg)" }}>
                expand_more
              </span>
            </button>
            {expandedSection === "sales-purchase" && (
              <div className="ml-8 pl-4 border-l border-stone-800 flex flex-col gap-2 mt-2">
                <div>
                  <p className="text-[10px] font-bold text-stone-550 uppercase tracking-widest px-3 mb-1">Sales</p>
                  <div className="flex flex-col gap-1 pl-2">
                    <NavLink 
                      to="/accounts/quotations"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                        isActive ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                      }`}
                    >
                      Quotation
                    </NavLink>
                    <NavLink 
                      to="/accounts/performa-invoices"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                        isActive ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                      }`}
                    >
                      Proforma Invoice
                    </NavLink>
                    <NavLink 
                      to="/accounts/sales-invoices"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                        isActive ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                      }`}
                    >
                      Sales Invoice
                    </NavLink>
                  </div>
                </div>

                <div>
                  <p className="text-[10px] font-bold text-stone-550 uppercase tracking-widest px-3 mb-1">Purchases</p>
                  <div className="flex flex-col gap-1 pl-2">
                    <NavLink 
                      to="/accounts/purchase-orders"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                        isActive ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                      }`}
                    >
                      Purchase Order (PO)
                    </NavLink>
                    <NavLink 
                      to="/accounts/purchase-invoices"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                        isActive ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                      }`}
                    >
                      Purchase Invoice
                    </NavLink>
                  </div>
                </div>

                <div>
                  <p className="text-[10px] font-bold text-stone-550 uppercase tracking-widest px-3 mb-1">Delivery Challans (DC)</p>
                  <div className="flex flex-col gap-1 pl-2">
                    <NavLink 
                      to="/accounts/delivery-challans?type=returnable"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                        isActive && location.search.includes("returnable") ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                      }`}
                    >
                      Returnable DC
                    </NavLink>
                    <NavLink 
                      to="/accounts/delivery-challans?type=non_returnable"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                        isActive && location.search.includes("non_returnable") ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                      }`}
                    >
                      Billing DC
                    </NavLink>
                    <NavLink 
                      to="/accounts/delivery-challans?type=inward"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                        isActive && location.search.includes("inward") ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                      }`}
                    >
                      Inward DC
                    </NavLink>
                  </div>
                </div>

                <NavLink 
                  to="/accounts/project-expenses"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                    isActive ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                  }`}
                >
                  Project Management
                </NavLink>
              </div>
            )}
          </div>

          {/* Accounts Accordion */}
          <div className="mb-2">
            <button
              type="button"
              onClick={() => setExpandedSection(prev => prev === "accounts" ? null : "accounts")}
              className={`w-[calc(100%-16px)] mx-2 py-2 px-4 flex items-center justify-between rounded-lg transition-colors border-2 ${
                ["/accounts/vouchers", "/accounts/accounts-monitor", "/accounts/gst-export-reports", "/accounts/debit-credit-notes", "/accounts/material-stock", "/accounts/product-mapping"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))
                  ? "bg-amber-500/10 text-amber-300 border-amber-500/30 font-bold"
                  : "text-stone-300 hover:text-white hover:bg-stone-900 border-transparent"
              }`}
            >
              <div className="flex items-center gap-4">
                <span className="material-symbols-outlined" style={{ fontVariationSettings: ["/accounts/vouchers", "/accounts/accounts-monitor", "/accounts/gst-export-reports", "/accounts/debit-credit-notes", "/accounts/material-stock", "/accounts/product-mapping"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`)) ? "'FILL' 1" : "'FILL' 0" }}>account_balance_wallet</span>
                <span className="font-label-caps text-label-caps">Accounts</span>
              </div>
              <span className="material-symbols-outlined transition-transform duration-200" style={{ transform: expandedSection === "accounts" ? "rotate(180deg)" : "rotate(0deg)" }}>
                expand_more
              </span>
            </button>
            {expandedSection === "accounts" && (
              <div className="ml-8 pl-4 border-l border-stone-800 flex flex-col gap-2 mt-2">
                <NavLink 
                  to="/accounts/vouchers"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                    isActive ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                  }`}
                >
                  Finance / Voucher Management
                </NavLink>
                <NavLink 
                  to="/accounts/gst-export-reports"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                    isActive ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                  }`}
                >
                  GST Export &amp; Reports
                </NavLink>
                <NavLink 
                  to="/accounts/debit-credit-notes"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                    isActive ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                  }`}
                >
                  Debit/Credit Notes
                </NavLink>
                <NavLink 
                  to="/accounts/material-stock"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                    isActive ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                  }`}
                >
                  Material Stock
                </NavLink>
                <NavLink 
                  to="/accounts/product-mapping"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                    isActive ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                  }`}
                >
                  Product Mapping
                </NavLink>
              </div>
            )}
          </div>



          {/* Contacts Accordion */}
          <div className="mb-2">
            <button
              type="button"
              onClick={() => setExpandedSection(prev => prev === "contacts" ? null : "contacts")}
              className={`w-[calc(100%-16px)] mx-2 py-2 px-4 flex items-center justify-between rounded-lg transition-colors border-2 ${
                ["/accounts/dealers", "/accounts/customers"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))
                  ? "bg-amber-500/10 text-amber-300 border-amber-500/30 font-bold"
                  : "text-stone-300 hover:text-white hover:bg-stone-900 border-transparent"
              }`}
            >
              <div className="flex items-center gap-4">
                <span className="material-symbols-outlined" style={{ fontVariationSettings: ["/accounts/dealers", "/accounts/customers"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`)) ? "'FILL' 1" : "'FILL' 0" }}>groups</span>
                <span className="font-label-caps text-label-caps">Contacts</span>
              </div>
              <span className="material-symbols-outlined transition-transform duration-200" style={{ transform: expandedSection === "contacts" ? "rotate(180deg)" : "rotate(0deg)" }}>
                expand_more
              </span>
            </button>
            {expandedSection === "contacts" && (
              <div className="ml-8 pl-4 border-l border-stone-800 flex flex-col gap-2 mt-2">
                <NavLink 
                  to="/accounts/dealers"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                    isActive ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                  }`}
                >
                  Suppliers
                </NavLink>
                <NavLink 
                  to="/accounts/customers"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                    isActive ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                  }`}
                >
                  Customers
                </NavLink>
                <NavLink 
                  to="/accounts/customer-groups"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                    isActive ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                  }`}
                >
                  Customer Groups
                </NavLink>
                <NavLink 
                  to="/accounts/abb-warranty-complaints"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                    isActive ? "text-amber-300 font-bold bg-amber-500/10" : "text-stone-400 hover:text-stone-100 hover:bg-stone-900"
                  }`}
                >
                  ABB Warranty Complaints
                </NavLink>
              </div>
            )}
          </div>
        </nav>

        {/* Profile and Logout links */}
        <div className="mt-auto px-2 space-y-1">
          <NavLink 
            to="/accounts/profile"
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) => `flex items-center gap-4 py-2 px-4 rounded-lg transition-colors border-2 ${
              isActive 
                ? "bg-amber-500 text-stone-950 border-amber-400 font-bold" 
                : "text-stone-300 hover:text-white hover:bg-stone-900 border-transparent"
            }`}
          >
            <span className="material-symbols-outlined">settings</span>
            <span className="font-label-caps text-label-caps">Settings</span>
          </NavLink>
          <button 
            onClick={handleLogout}
            className="w-full text-left text-stone-300 hover:text-white hover:bg-stone-900 flex items-center gap-4 py-2 px-4 rounded-lg transition-colors border-2 border-transparent"
          >
            <span className="material-symbols-outlined">logout</span>
            <span className="font-label-caps text-label-caps">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className={`min-h-screen transition-all duration-300 pb-8 ${
        sidebarOpen ? "lg:pl-[280px]" : "lg:pl-0"
      }`}>
        {/* Top Sticky Header */}
        <header className="flex justify-between items-center w-full px-8 py-4 gap-md sticky top-0 z-30 backdrop-blur-md bg-white/90 border-b border-stone-100">
          <div className="flex items-center gap-4">
            <button 
              data-sidebar-toggle="true"
              className="p-2 hover:bg-stone-100 rounded-full transition-colors active:opacity-80 flex items-center justify-center" 
              onClick={() => setSidebarOpen(prev => !prev)}
            >
              <span className="material-symbols-outlined text-stone-800">menu</span>
            </button>
            <span className="font-label-caps text-label-caps text-stone-600/50">ACCOUNTS OS</span>
          </div>

          <div className="flex items-center gap-6">
            {/* Notifications Dropdown */}
            <div ref={desktopNotifRef} className="relative">
              <button 
                onClick={() => setNotifOpen(c => !c)}
                className="relative cursor-pointer hover:bg-amber-100 p-2 transition-colors rounded-full active:opacity-80 flex items-center justify-center"
              >
                <span className="material-symbols-outlined text-amber-850">notifications</span>
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 top-full mt-2 z-30 w-80 max-h-[24rem] overflow-y-auto rounded-2xl border border-amber-100 bg-white p-3 shadow-lg">
                  <div className="mb-2 flex items-center justify-between border-b border-amber-100 pb-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-stone-500">Notifications</p>
                    {notifications.length > 0 && (
                      <button onClick={clearAllNotifications} className="text-[11px] font-semibold text-red-500 hover:text-red-700">Clear all</button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <p className="py-4 text-center text-sm text-stone-500">You're all caught up!</p>
                  ) : notifications.map(n => (
                    <button 
                      key={n._id} 
                      onClick={() => {
                        markRead(n);
                      }} 
                      className={`mb-2 w-full rounded-xl px-3 py-3 text-left transition-colors border ${n.isRead ? "bg-stone-50 border-stone-100" : "bg-amber-50 border-amber-100"}`}
                    >
                      <p className="font-semibold text-stone-900 text-sm leading-snug">{n.title || n.message}</p>
                      {n.title && <p className="text-xs text-stone-500 mt-1 line-clamp-2">{n.message}</p>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Profile Dropdown */}
            <div ref={desktopProfileRef} className="relative">
              <button 
                onClick={() => setProfileOpen(c => !c)}
                className="flex items-center gap-3 bg-white px-4 py-2 rounded-full border border-amber-200 hover:bg-amber-100/50 transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-stone-950 font-bold text-sm">
                  {accountsName.charAt(0).toUpperCase()}
                </div>
                <div className="flex flex-col">
                  <span className="font-label-caps text-[10px] text-stone-500 leading-none">LOGGED IN</span>
                  <span className="font-body-md text-body-md font-bold text-stone-800 mt-0.5">{accountsName}</span>
                </div>
              </button>
              {profileOpen && (
                <div className="absolute right-0 z-30 mt-2 w-56 rounded-2xl border border-amber-100 bg-white p-2 shadow-lg">
                  <button 
                    onClick={() => { setProfileOpen(false); navigate("/accounts/profile"); }} 
                    className="w-full rounded-xl px-4 py-3 text-left text-sm hover:bg-gray-50 font-semibold text-stone-700 transition-colors"
                  >
                    My Profile
                  </button>
                  <button 
                    onClick={handleLogout} 
                    className="w-full rounded-xl px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 font-semibold transition-colors"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content Canvas */}
        <div className="p-8 w-full max-w-none page-canvas">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
